/**
 * Colour helpers for the colour swatches (measured sample vs. target).
 *
 * The backend returns L / Chroma / Hue (and, when available, the tristimulus
 * X, Y, Z they derive from). We turn either of those into an RGB hex colour.
 *
 * NOTE: the XYZ -> RGB conversion below mirrors the in-house Octave function
 * `XYZtoRGB.m` used by the lab (Adobe RGB (1998) matrix, D65 white, gamma 2.2),
 * so the swatch colours match the lab's numbers. Browsers interpret the
 * result as sRGB, so colours look slightly less saturated than a true sRGB
 * conversion would.
 */

export interface XYZ { X: number; Y: number; Z: number; }
export interface RGB { r: number; g: number; b: number; } // 0..255

/** D65 reference white (Y normalised to 100). */
const D65 = { X: 95.047, Y: 100.0, Z: 108.883 };

/**
 * Anti-reflective coatings reflect ~1% of the light, so the *absolute* colour
 * of the residual reflection is almost black. To make the swatches readable we
 * keep chromaticity (hue + saturation) and scale luminance so that the brighter
 * of the two samples is displayed at `DISPLAY_Y`. Both swatches share the same
 * factor, so "measured is brighter/darker than target" is preserved.
 * Set NORMALIZE_LUMINANCE to false to display the absolute colour instead.
 */
export const NORMALIZE_LUMINANCE = true;
export const DISPLAY_Y = 0.45; // 0..1, relative to a Y=1 white

/**
 * Scale of the X, Y, Z values returned by the backend: 100 means "Y = 100 for a
 * perfect white" (same convention as L*, and as Rvis in %). Set to 1 if the
 * script returns them normalised to 1. Irrelevant while NORMALIZE_LUMINANCE is
 * on, since only the ratio between both samples matters then.
 */
export const XYZ_INPUT_SCALE = 100;

/** CIE L*C*h (hue in degrees) -> XYZ (Y of white = 1, D65). */
export function lchToXyz(L: number, C: number, h: number): XYZ {
    const rad = h * Math.PI / 180;
    const a = C * Math.cos(rad);
    const b = C * Math.sin(rad);
    return labToXyz(L, a, b);
}

/** CIE L*a*b* -> XYZ (Y of white = 1, D65). */
export function labToXyz(L: number, a: number, b: number): XYZ {
    const fy = (L + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - b / 200;
    const finv = (t: number) => {
        const t3 = t * t * t;
        return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787;
    };
    return {
        X: D65.X / 100 * finv(fx),
        Y: D65.Y / 100 * finv(fy),
        Z: D65.Z / 100 * finv(fz),
    };
}

/**
 * XYZ -> RGB (0..255). `xyz` is expected with Y of white = 1.
 * Same matrix and gamma as the lab's `XYZtoRGB.m` (Adobe RGB (1998), D65).
 * Out-of-gamut components are clipped.
 */
export function xyzToRgb(xyz: XYZ): RGB {
    const { X, Y, Z } = xyz;
    let r = 2.0413690 * X - 0.5649464 * Y - 0.3446944 * Z;
    let g = -0.9692660 * X + 1.8760108 * Y + 0.0415560 * Z;
    let b = 0.0134474 * X - 0.1183897 * Y + 1.0154096 * Z;

    const gamma = (c: number) => {
        c = Math.min(1, Math.max(0, c));
        return Math.pow(c, 1 / 2.2);
    };
    return {
        r: Math.round(gamma(r) * 255),
        g: Math.round(gamma(g) * 255),
        b: Math.round(gamma(b) * 255),
    };
}

export function rgbToHex(rgb: RGB): string {
    const h = (v: number) => ('0' + v.toString(16)).slice(-2);
    return '#' + h(rgb.r) + h(rgb.g) + h(rgb.b);
}

/**
 * Converts a pair (current, target) of XYZ colours (Y of white = 1) into hex
 * strings ready for the swatches. Either can be null when not available.
 */
export function swatchPair(current: XYZ | null, target: XYZ | null): { current: string | null, target: string | null } {
    let cur = current;
    let tgt = target;

    if (NORMALIZE_LUMINANCE) {
        const maxY = Math.max(cur ? cur.Y : 0, tgt ? tgt.Y : 0);
        if (maxY > 0) {
            const k = DISPLAY_Y / maxY;
            const scale = (c: XYZ) => ({ X: c.X * k, Y: c.Y * k, Z: c.Z * k });
            cur = cur ? scale(cur) : null;
            tgt = tgt ? scale(tgt) : null;
        }
    }

    return {
        current: cur ? rgbToHex(xyzToRgb(cur)) : null,
        target: tgt ? rgbToHex(xyzToRgb(tgt)) : null,
    };
}

/**
 * Reads the XYZ of a sample (Y of white = 1) from a `{key: value}` map of job
 * parameters. Prefers X, Y, Z when present; otherwise derives them from
 * L, Chroma, Hue.
 */
export function xyzFromParams(p: { [key: string]: number }): XYZ | null {
    const get = (...names: string[]): number | null => {
        for (const n of names) {
            const k = Object.keys(p).find(key => key.toLowerCase() === n.toLowerCase());
            if (k !== undefined && isFinite(p[k])) { return Number(p[k]); }
        }
        return null;
    };

    const X = get('X'), Y = get('Y'), Z = get('Z');
    if (X !== null && Y !== null && Z !== null) {
        return { X: X / XYZ_INPUT_SCALE, Y: Y / XYZ_INPUT_SCALE, Z: Z / XYZ_INPUT_SCALE };
    }

    const L = get('L'), C = get('Chroma', 'C'), h = get('Hue', 'h');
    if (L !== null && C !== null && h !== null) {
        return lchToXyz(L, C, h);
    }
    return null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Preliminary colour from the measured spectrum (client side).
 *
 * As soon as a measurement file is imported we can integrate its reflectance
 * curve against the CIE 1931 2° colour matching functions under illuminant
 * D65 and get X, Y, Z without waiting for the backend job. The curve is used
 * as-is: no mirror-reference correction is applied, so for instruments that
 * measure relative to a reference mirror the preliminary colour is only an
 * approximation. It gets replaced by the backend values when the job returns.
 * ──────────────────────────────────────────────────────────────────────────── */

/** CIE 1931 2° standard observer, 380..780 nm in 10 nm steps: [x, y, z]. */
const CMF_START = 380;
const CMF_STEP = 10;
const CMF_2DEG: [number, number, number][] = [
    [0.001368, 0.000039, 0.006450], // 380
    [0.004243, 0.000120, 0.020050], // 390
    [0.014310, 0.000396, 0.067850], // 400
    [0.043510, 0.001210, 0.207400], // 410
    [0.134380, 0.004000, 0.645600], // 420
    [0.283900, 0.011600, 1.385600], // 430
    [0.348280, 0.023000, 1.747060], // 440
    [0.336200, 0.038000, 1.772110], // 450
    [0.290800, 0.060000, 1.669200], // 460
    [0.195360, 0.090980, 1.287640], // 470
    [0.095640, 0.139020, 0.812950], // 480
    [0.032010, 0.208020, 0.465180], // 490
    [0.004900, 0.323000, 0.272000], // 500
    [0.009300, 0.503000, 0.158200], // 510
    [0.063270, 0.710000, 0.078250], // 520
    [0.165500, 0.862000, 0.042160], // 530
    [0.290400, 0.954000, 0.020300], // 540
    [0.433450, 0.994950, 0.008750], // 550
    [0.594500, 0.995000, 0.003900], // 560
    [0.762100, 0.952000, 0.002100], // 570
    [0.916300, 0.870000, 0.001650], // 580
    [1.026300, 0.757000, 0.001100], // 590
    [1.062200, 0.631000, 0.000800], // 600
    [1.002600, 0.503000, 0.000340], // 610
    [0.854450, 0.381000, 0.000190], // 620
    [0.642400, 0.265000, 0.000050], // 630
    [0.447900, 0.175000, 0.000020], // 640
    [0.283500, 0.107000, 0.000000], // 650
    [0.164900, 0.061000, 0.000000], // 660
    [0.087400, 0.032000, 0.000000], // 670
    [0.046770, 0.017000, 0.000000], // 680
    [0.022700, 0.008210, 0.000000], // 690
    [0.011359, 0.004102, 0.000000], // 700
    [0.005790, 0.002091, 0.000000], // 710
    [0.002899, 0.001047, 0.000000], // 720
    [0.001440, 0.000520, 0.000000], // 730
    [0.000690, 0.000249, 0.000000], // 740
    [0.000332, 0.000120, 0.000000], // 750
    [0.000166, 0.000060, 0.000000], // 760
    [0.000083, 0.000030, 0.000000], // 770
    [0.000042, 0.000015, 0.000000], // 780
];

/** CIE illuminant D65 relative spectral power, same grid as CMF_2DEG. */
const D65_SPD: number[] = [
    49.9755, 54.6482, 82.7549, 91.4860, 93.4318, 86.6823, 104.8650, 117.0080,
    117.8120, 114.8610, 115.9230, 108.8110, 109.3540, 107.8020, 104.7900, 107.6890,
    104.4050, 104.0460, 100.0000, 96.3342, 95.7880, 88.6856, 90.0062, 89.5991,
    87.6987, 83.2886, 83.6992, 80.0268, 80.2146, 82.2778, 78.2842, 69.7213,
    71.6091, 74.3490, 61.6040, 69.8856, 75.0870, 63.5927, 46.4182, 66.8054,
    63.3828,
];

/**
 * The measured curve must cover at least this range for the preliminary
 * colour to be computed; outside the file's range the curve is extended flat.
 */
const MIN_COVERAGE = { from: 420, to: 680 };

/**
 * Measurement files may express reflectance in % (0..100) or as a fraction
 * (0..1). A lens never averages more than ~10 % over the visible, and an
 * AR coating in % never averages below ~0.3 %, so the mean value separates
 * both conventions safely. Only affects L* / Rvis; the swatch is normalised.
 */
const PERCENT_IF_MEAN_ABOVE = 0.2;

/** Linear interpolation of an ascending [wavelength, value] list at `x`, flat outside. */
function interp(points: [number, number][], x: number): number {
    if (x <= points[0][0]) { return points[0][1]; }
    const last = points[points.length - 1];
    if (x >= last[0]) { return last[1]; }
    let lo = 0, hi = points.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (points[mid][0] <= x) { lo = mid; } else { hi = mid; }
    }
    const x0 = points[lo][0], y0 = points[lo][1];
    const x1 = points[hi][0], y1 = points[hi][1];
    return x1 === x0 ? y0 : y0 + (y1 - y0) * (x - x0) / (x1 - x0);
}

/**
 * XYZ (Y of white = 1, D65 / 2°) of a reflectance spectrum given as
 * [wavelength nm, reflectance] pairs in any order. Returns null when the
 * curve is empty, non-numeric or does not cover the visible range.
 */
export function xyzFromSpectrum(wave: number[][]): XYZ | null {
    const pts: [number, number][] = (wave || [])
        .filter(p => p && isFinite(p[0]) && isFinite(p[1]))
        .map(p => [Number(p[0]), Number(p[1])] as [number, number])
        .sort((a, b) => a[0] - b[0]);
    if (pts.length < 2) { return null; }
    if (pts[0][0] > MIN_COVERAGE.from || pts[pts.length - 1][0] < MIN_COVERAGE.to) { return null; }

    const mean = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const scale = mean > PERCENT_IF_MEAN_ABOVE ? 100 : 1;

    let X = 0, Y = 0, Z = 0, N = 0;
    CMF_2DEG.forEach((cmf, i) => {
        const nm = CMF_START + i * CMF_STEP;
        const S = D65_SPD[i];
        const R = interp(pts, nm) / scale;
        X += S * R * cmf[0];
        Y += S * R * cmf[1];
        Z += S * R * cmf[2];
        N += S * cmf[1];
    });
    return { X: X / N, Y: Y / N, Z: Z / N };
}

export interface LCh { L: number; C: number; h: number; }

/** XYZ (Y of white = 1, D65) -> CIE L*C*h (hue in degrees, 0..360). */
export function xyzToLch(xyz: XYZ): LCh {
    const f = (t: number) => t > 0.008856 ? Math.pow(t, 1 / 3) : 7.787 * t + 16 / 116;
    const fx = f(xyz.X / (D65.X / 100));
    const fy = f(xyz.Y / (D65.Y / 100));
    const fz = f(xyz.Z / (D65.Z / 100));
    const L = 116 * fy - 16;
    const a = 500 * (fx - fy);
    const b = 200 * (fy - fz);
    let h = Math.atan2(b, a) * 180 / Math.PI;
    if (h < 0) { h += 360; }
    return { L: L, C: Math.sqrt(a * a + b * b), h: h };
}
